import { supabase } from '../lib/supabase';

export const emailSequenceService = {
  // Get all email sequences
  async getAllSequences() {
    const { data, error } = await supabase
      ?.from('email_sequences')
      ?.select(`
        *,
        email_sequence_steps(
          id,
          step_order,
          delay_hours,
          subject_template,
          html_template,
          is_active
        )
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Get sequence by ID with steps
  async getSequenceById(sequenceId) {
    const { data, error } = await supabase
      ?.from('email_sequences')
      ?.select(`
        *,
        email_sequence_steps(
          id,
          step_order,
          delay_hours,
          subject_template,
          html_template,
          is_active,
          created_at
        )
      `)
      ?.eq('id', sequenceId)
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Get sequences by form type
  async getSequencesByFormType(formType) {
    const { data, error } = await supabase
      ?.from('email_sequences')
      ?.select(`
        *,
        email_sequence_steps(
          id,
          step_order,
          delay_hours,
          subject_template,
          is_active
        )
      `)
      ?.eq('form_type', formType)
      ?.eq('is_active', true);
    
    if (error) throw error;
    return data;
  },

  // Create new email sequence
  async createSequence(sequenceData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase
      ?.from('email_sequences')
      ?.insert({
        name: sequenceData?.name,
        description: sequenceData?.description,
        form_type: sequenceData?.formType,
        trigger_type: sequenceData?.triggerType || 'form_submission',
        is_active: sequenceData?.isActive !== undefined ? sequenceData?.isActive : true,
        created_by: user?.id
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Update email sequence
  async updateSequence(sequenceId, updates) {
    const { data, error } = await supabase
      ?.from('email_sequences')
      ?.update({
        name: updates?.name,
        description: updates?.description,
        is_active: updates?.isActive
      })
      ?.eq('id', sequenceId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Delete email sequence
  async deleteSequence(sequenceId) {
    const { error } = await supabase
      ?.from('email_sequences')
      ?.delete()
      ?.eq('id', sequenceId);
    
    if (error) throw error;
  },

  // Create sequence step
  async createStep(stepData) {
    const { data, error } = await supabase
      ?.from('email_sequence_steps')
      ?.insert({
        sequence_id: stepData?.sequenceId,
        step_order: stepData?.stepOrder,
        delay_hours: stepData?.delayHours,
        subject_template: stepData?.subjectTemplate,
        html_template: stepData?.htmlTemplate,
        is_active: stepData?.isActive !== undefined ? stepData?.isActive : true
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Update sequence step
  async updateStep(stepId, updates) {
    const { data, error } = await supabase
      ?.from('email_sequence_steps')
      ?.update({
        step_order: updates?.stepOrder,
        delay_hours: updates?.delayHours,
        subject_template: updates?.subjectTemplate,
        html_template: updates?.htmlTemplate,
        is_active: updates?.isActive
      })
      ?.eq('id', stepId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Delete sequence step
  async deleteStep(stepId) {
    const { error } = await supabase
      ?.from('email_sequence_steps')
      ?.delete()
      ?.eq('id', stepId);
    
    if (error) throw error;
  },

  // Get tracking data for a submission
  async getTrackingBySubmission(submissionId) {
    const { data, error } = await supabase
      ?.from('email_sequence_tracking')
      ?.select(`
        *,
        email_sequence_steps(
          step_order,
          delay_hours,
          subject_template
        ),
        email_sequences(
          name
        )
      `)
      ?.eq('submission_id', submissionId)
      ?.order('scheduled_at', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  // Get all tracking data with filters
  async getAllTracking(filters = {}) {
    let query = supabase
      ?.from('email_sequence_tracking')
      ?.select(`
        *,
        email_sequence_steps(
          step_order,
          subject_template
        ),
        email_sequences(
          name,
          form_type
        ),
        form_submissions(
          name,
          email,
          form_type
        )
      `);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.sequenceId) {
      query = query?.eq('sequence_id', filters?.sequenceId);
    }

    query = query?.order('scheduled_at', { ascending: false });

    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  },

  // Schedule sequence for a form submission
  async scheduleSequence(submissionId, formType) {
    const { error } = await supabase?.rpc('schedule_email_sequence', {
      p_submission_id: submissionId,
      p_form_type: formType
    });
    
    if (error) throw error;
  },

  // Get pending emails ready to send
  async getPendingEmails() {
    const { data, error } = await supabase?.rpc('get_pending_sequence_emails');
    
    if (error) throw error;
    return data;
  },

  // Mark email as sent
  async markEmailSent(trackingId, success, errorMessage = null) {
    const { error } = await supabase?.rpc('mark_sequence_email_sent', {
      p_tracking_id: trackingId,
      p_success: success,
      p_error_message: errorMessage
    });
    
    if (error) throw error;
  },

  // Cancel scheduled email
  async cancelScheduledEmail(trackingId) {
    const { error } = await supabase
      ?.from('email_sequence_tracking')
      ?.update({ status: 'cancelled' })
      ?.eq('id', trackingId);
    
    if (error) throw error;
  },

  // Get sequence statistics
  async getSequenceStats(sequenceId) {
    const { data, error } = await supabase
      ?.from('email_sequence_tracking')
      ?.select('status')
      ?.eq('sequence_id', sequenceId);
    
    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      pending: data?.filter(t => t?.status === 'pending')?.length || 0,
      sent: data?.filter(t => t?.status === 'sent')?.length || 0,
      failed: data?.filter(t => t?.status === 'failed')?.length || 0,
      cancelled: data?.filter(t => t?.status === 'cancelled')?.length || 0
    };

    return stats;
  },

  // Render template with form data
  renderTemplate(template, formData) {
    if (!template || !formData) return '';

    return template?.replace(/\{\{([^}]+)\}\}/g, (match, fieldPath) => {
      const trimmedPath = fieldPath?.trim();
      
      // Handle nested fields (e.g., metadata.service)
      if (trimmedPath?.includes('.')) {
        const parts = trimmedPath?.split('.');
        let value = formData;
        
        for (const part of parts) {
          value = value?.[part];
          if (value === undefined || value === null) {
            return '';
          }
        }
        
        return value || '';
      }
      
      // Handle direct fields
      return formData?.[trimmedPath] || '';
    });
  }
};